import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '@base/base.component';
import { CustomValidators } from '@forms/custom-validators';
import { ListDealerBirthDatesQueryResponse } from '@models/reports/reports';
import { KeyValueOptions } from '@models/settings/settings';
import { Paginator, PaginatorItems, Table } from '@models/table';
import { ReportsService } from '@services/reports.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { BirthdateDetailsModalComponent } from './modals/birthdate-details-modal/birthdate-details-modal.component';
import { PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_EXPORT_PDF, PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_EXPORT_XLSX, PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_READ, PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_VIEWDETAILS } from 'src/app/const/permitions';
import { DateHelper } from '@helpers/data-helper';
import { formatNumberToLocal } from 'src/app/utils/format-number-to-local';

@Component({
  selector: 'app-birthdate-report',
  templateUrl: './birthdate-report.component.html',
  styleUrl: './birthdate-report.component.scss'
})
export class BirthdateReportComponent extends BaseComponent implements OnInit {
  private accessPointChangeSubscription: Subscription;
  private firstSearchDone: boolean = false;

  public readonly table: Table<any> = this.createTable();

  public readonly monthOptions: KeyValueOptions[] = [
    {
      key: "Todos",
      value: 0
    },
    {
      key: "Janeiro",
      value: 1,
    },
    {
      key: "Fevereiro",
      value: 2
    },
    {
      key: "Março",
      value: 3
    },
    {
      key: "Abril",
      value: 4
    },
    {
      key: "Maio",
      value: 5
    },
    {
      key: "Junho",
      value: 6
    },
    {
      key: "Julho",
      value: 7
    },
    {
      key: "Agosto",
      value: 8
    },
    {
      key: "Setembro",
      value: 9
    },
    {
      key: "Outubro",
      value: 10
    },
    {
      key: "Novembro",
      value: 11
    },
    {
      key: "Dezembro",
      value: 12
    }
  ]

  public dealerOptions: KeyValueOptions[] = [
    {
      key: "Todos",
      value: ""
    }
  ]

  public form: FormGroup;
  public loading: boolean = false;
  public isExportingExcel: boolean = false;
  public isExportingPdf: boolean = false;

  public constructor(private readonly _fb: FormBuilder,
    private readonly _datePipe: DatePipe,
    private readonly _reportsService: ReportsService,
  ) {
    super();
  }

  public ngOnInit() {
    this.listDealers();
    this.createForm();
    this.accessPointChangeSubscription = this.acessPointChangeEvent().subscribe(() => this.listDealers());

    if (!this.hasAccessToAnyDealer) {
      this.openDialogMessage("Não foram encontrados nenhum ponto de venda para sua conta, por esse motivo vamos bloquear a pesquisa deste relatório.")
    }
  }

  public get hasAccessToAnyDealer() {
    return this.getAllAllowedDealers().length > 0;
  }

  public get hasPermissionToViewDetails() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_VIEWDETAILS].includes(p))
  }

  public get hasPermissionToExportExcel() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_EXPORT_XLSX].includes(p));
  }

  public get hasPermissionToExportPdf() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_EXPORT_PDF].includes(p))
  }

  public get hasPermissionToRead() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_REPORTS_BIRTHDATE_READ].includes(p));
  }

  private createForm() {
    this.form = this._fb.group({
      birthMonthIndex: [0, [Validators.required]],
      identifierCode: [0, [Validators.nullValidator]],
      startDate: [DateHelper.SubstractDaysFromDate(new Date(), 30), [Validators.required]],
      endDate: [new Date(), [Validators.required]]
    });

    this.form.setValidators([
      CustomValidators.isStartDateGreaterThanEndDate("startDate", "endDate"),
      CustomValidators.periodGreaterThan(30, 'startDate', 'endDate')
    ]);
  }


  public exporQueryExcel(): void {
    this.isExportingExcel = true;

    firstValueFrom(
      this._reportsService.exportBirthDateReport(this.form.value)
    ).then(res => {
      this.download(res.fileBase64, res.fileName, res.contentType);
    })
      .catch(er => {
        this.openErrorDialog("Erro ao exportar excel", er.message);
      })
      .finally(() => {
        this.isExportingExcel = false;
      })
  }

  public filter(paginator?: Paginator): void {
    if (!this.hasAccessToAnyDealer || this.form.invalid) {
      return;
    }

    this.loading = true;

    if (!paginator) {
      paginator = this.table.paginator;
    }

    firstValueFrom(
      this._reportsService.getBirthDateReport(this.form.value, paginator, this.firstSearchDone)
    )
      .then((resp: PaginatorItems<ListDealerBirthDatesQueryResponse>) => {
        this.table.data = resp.items;
        this.table.paginator.length = resp.totalCount;
        this.table.paginator.index = paginator.index;
        this.table.paginator.pageSize = paginator.pageSize;
      })
      .finally(() => {
        this.loading = false;
        this.firstSearchDone = true;
      });
  }

  public async listDealers() {
    const allowedDealersOptions = this.getAllAllowedDealersForCurrentHierachy()
      .filter((item) => item.isEconomicGroup === false)
      .map<KeyValueOptions>(us => ({
        key: `${us.identifierCode} - ${us.description}`,
        value: us.identifierCode
      }));

    this.dealerOptions = [
      {
        key: "Todos",
        value: 0
      }, ...allowedDealersOptions];

    if (this.form) {
      this.form.patchValue({
        identifierCode: 0
      });
    }
  }

  public cleanForm(): void {
    this.form.patchValue({
      birthMonthIndex: 0,
      identifierCode: 0,
      startDate: DateHelper.SubstractDaysFromDate(new Date(), 30),
      endDate: new Date()
    });

    this.table.data = [];
  }

  public async exportWorksheetReport(): Promise<void> {
    this.isExportingExcel = true;
    try {
      const result = await firstValueFrom(this._reportsService.exportBirthDateReport(this.form.value));
      this.download(result.fileBase64, result.fileName, result.contentType);
    } finally {
      this.isExportingExcel = false;
    }
  }

  public async exportPdfReport(): Promise<void> {
    this.isExportingPdf = true;
    try {
      const result = await firstValueFrom(this._reportsService.exportBirthDatePdf(this.form.value));

      this.download(result, "relatorio-aniversariantes.pdf", "application/pdf");
    } finally {
      this.isExportingPdf = false;
    }
  }

  public pageChange(paginator: Paginator): void {
    this.filter(paginator);
  }

  public viewBirthDateDetails(birthDate: ListDealerBirthDatesQueryResponse): void {
    this.openDialog(BirthdateDetailsModalComponent, { data: birthDate.userId });
  }


  private createTable(): Table<any> {
    return {
      headers: [
        {
          key: "birthDate",
          value: "Data de nascimento",
        },
        {
          key: "contract",
          value: "Contrato",
        },
        {
          key: "group",
          value: "Grupo",
        },
        {
          key: "quota",
          value: "Cota",
        },
        {
          key: "version",
          value: "Versão",
        },
        {
          key: "sale",
          value: "Venda",
        },
        {
          key: "sellerDocument",
          value: "CPF Vendedor",
        },
        {
          key: "asset",
          value: "Bem"
        },
        {
          key: "credit",
          value: "Crédito"
        },
        {
          key: "contemplation",
          value: "Contemplação"
        },
        {
          key: "clientName",
          value: "Nome cliente"
        }
      ],
      data: [],
      style: {
        table: {
          height: "500px",
        },
        data: [
          {
            key: "credit",
            transform: (value) => value && formatNumberToLocal(value)
          },
          {
            key: "birthDate",
            transform: (value) =>
              this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "sale",
            transform: (value) =>
              this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "contemplation",
            transform: (value) => !!value ?
              this._datePipe.transform(value, "dd/MM/yyyy") : "Não contemplado",
          }
        ],
      },
      buttons: this.hasPermissionToViewDetails && {
        read: true
      },
      paginator: {
        index: 1,
        itensPerPage: [10, 15, 20, 50, 100],
        pageSize: 10,
      },
      messageWithoutDates: "Não há dados para exibir no momento.",
    };
  }
}
